/**
 * AT1 Feedback Storage
 *
 * Helpers pour gérer le localStorage des feedbacks AT1.
 * Permet de s'assurer qu'un agent ne vote qu'une seule fois par dossier.
 */

/**
 * ============================================================================
 * AB TEST: Maturity AT1 Feedback Experiment (ab_at1_feedback)
 * PR: #2501
 *
 * This file is part of an A/B test experiment for collecting user feedback
 * on automatically generated AT1 messages. When the experiment concludes,
 * this entire file should be either:
 * - Removed if the experiment is unsuccessful
 * - Renamed (remove AB_ prefix) and integrated permanently if successful
 *
 * To find all AB test code: grep -r "ab_at1_feedback" src/
 * ============================================================================
 */

const STORAGE_KEY = 'at1_feedbacks';

export interface AT1FeedbackData {
    messageId: string | number;
    voted: boolean;
    feedbackValue: 1 | 0;
    submittedAt: string; // ISO date
}

export interface AT1FeedbackStorage {
    [dossierId: string]: AT1FeedbackData;
}

/**
 * Récupère tous les feedbacks stockés
 */
const getAllFeedbacks = (): AT1FeedbackStorage => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch (error) {
        console.error('[AT1 Feedback] Error reading from localStorage:', error);
        return {};
    }
};

/**
 * Vérifie si l'agent a déjà voté pour ce dossier
 */
export const hasVotedForDossier = (dossierId: string | number): boolean => {
    try {
        const feedbacks = getAllFeedbacks();
        return !!feedbacks[String(dossierId)]?.voted;
    } catch {
        return false;
    }
};

/**
 * Sauvegarde un feedback dans le localStorage
 */
export const saveFeedback = (
    dossierId: string | number,
    data: Omit<AT1FeedbackData, 'submittedAt'>,
): void => {
    try {
        const feedbacks = getAllFeedbacks();

        feedbacks[String(dossierId)] = {
            ...data,
            submittedAt: new Date().toISOString(),
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(feedbacks));
    } catch (error) {
        console.error('[AT1 Feedback] Error saving to localStorage:', error);
    }
};

/**
 * Récupère le feedback pour un dossier spécifique
 */
export const getFeedbackForDossier = (dossierId: string | number): AT1FeedbackData | null => {
    try {
        const feedbacks = getAllFeedbacks();
        return feedbacks[String(dossierId)] || null;
    } catch {
        return null;
    }
};

/**
 * Supprime le feedback pour un dossier (utile pour debug/tests)
 */
export const clearFeedbackForDossier = (dossierId: string | number): void => {
    try {
        const feedbacks = getAllFeedbacks();
        delete feedbacks[String(dossierId)];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(feedbacks));
    } catch (error) {
        console.error('[AT1 Feedback] Error clearing from localStorage:', error);
    }
};
